import { AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, Box, Button, Checkbox, Flex, Grid, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Tbody, Text, Tfoot, Th, Thead, useBoolean, useDisclosure, useToast } from '@chakra-ui/react'
import type { InsuranceParamsPoleasGridItem, InsuranceParamsVehicleGridItem } from 'BrokerTsLib/src/dtos'
import { ImportInsuranceStates, formatPersonFullName, formatPersonRcIco } from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import { useContext, useRef, useState } from 'react'

import { Pagination, Table, TableData, TableHeader, TableRow } from '../../components/Grid'
import { Loading } from '../../components/Loading'
import { useApp } from '../../hooks/useApp'
import { useAddClientDisabledFlag, useDeleteInsuranceParams, useGeneratePublicWizardUrl, useGetPoleasExportUrl, useMarkForExport, useRemoveClientDisabledFlag, useSendPoleasPublicWizardAccess } from '../../hooks/usePoleas'

import { useAuth } from '@/context/AuthContext'
import { usePartner } from '@/context/PartnerContext'
import type { ClientDisabledRequest } from '@/dtos/PoleasImport'
import { poleasContext } from '@/pages/poleas/PoleasImportsPage'
import type { BlockClientAccessFormValues } from './forms/BlockClientAccessForm'
import { BlockClientAccessForm } from './forms/BlockClientAccessForm'
import { ImportRow } from './ImportRow'

export const TableWrapper = ({ children }: { children: React.ReactNode }) => {
  return <Box sx={{ maxW: '100%', overflow: 'hidden', overflowX: 'auto' }}>{children}</Box>
}

type ImportsListProps = {
  loading?: boolean
  items: InsuranceParamsPoleasGridItem[]
  withPaginator?: boolean
  allowSelect?: boolean
  leftFooterPart?: React.ReactNode
}

type SingleRowModalProps = {
  kind: SingleRowModalKind
  title: string
  bodyText: string
  bodyUrl?: string
  okButtonText: string
  showCancelButton: boolean
}

const SingleRowModalKind = {
  DeleteRow: 1,
  SendAccess: 2,
  PublicUrl: 3,
  Unknown: 0
}
export type SingleRowModalKind = typeof SingleRowModalKind[keyof typeof SingleRowModalKind]

export const ImportsList = ({
  loading,
  items: items,
  leftFooterPart,
  withPaginator = false,
  allowSelect = false,
}: ImportsListProps) => {

  const { currentPage, pageSize, totalCount, filter, setCurrentPage, reload, ...rest } = useContext(poleasContext)
  loading = loading || rest.loading

  const { product } = useApp()
  const { user } = useAuth()

  const [selectedRows, setSelectedRows] = useState<InsuranceParamsVehicleGridItem['insuranceParamsId'][]>([])
  const [confirmDelete, setConfirmDelete] = useBoolean(false)
  const [isDeleting, setIsDeleting] = useBoolean(false)
  const [confirmSendAccess, setConfirmSendAccess] = useBoolean(false)
  const [isSendingAccess, setIsSendingAccess] = useBoolean(false)
  const [confirmExport, setConfirmExport] = useBoolean(false)
  const [isExporting, setIsExporting] = useBoolean(false)
  const isIndeterminate = selectedRows.length > 0 && selectedRows.length < items.length
  const isChecked = selectedRows.length === items.length
  const toast = useToast()
  const { mutateAsync: deleteInsuranceParams } = useDeleteInsuranceParams()
  const { mutateAsync: removeClientDisabledFlag } = useRemoveClientDisabledFlag()
  const { mutateAsync: addClientDisabledFlag } = useAddClientDisabledFlag()
  const { mutateAsync: sendAccess } = useSendPoleasPublicWizardAccess()
  const { mutateAsync: generatePublicWizardUrl } = useGeneratePublicWizardUrl()
  const { mutateAsync: markForExport } = useMarkForExport()
  const getPoleasExportUrl = useGetPoleasExportUrl()

  const { getOrigAppUrl } = usePartner()
  const origAppUrl = getOrigAppUrl()

  const defaultSingleRowModalProps: SingleRowModalProps = {
    kind: SingleRowModalKind.Unknown,
    bodyText: '',
    showCancelButton: false,
    okButtonText: 'Ok',
    title: ''
  }

  const [modalParamsId, setModalParamsId] = useState('0')
  const [singleRowModalProps, setSingleRowModalProps] = useState(defaultSingleRowModalProps)
  const singleRowModal = useDisclosure()
  const sigleRowModalCancelRef = useRef<any>()

  const blockOfferModal = useDisclosure()
  const [blockOfferUser, setBlockOfferUser] = useState('')

  let deletedCountText = undefined
  if (filter.importId) {
    const deletedCount = items.filter(it => it.state == ImportInsuranceStates.Error).length
    if (deletedCount > 0) {
      deletedCountText = <Text as='span' sx={{ color: 'red', ml: 2 }}>{`(chybné: ${deletedCount})`}</Text>
    }
  }

  const handleCheckboxChange = () => {
    if (selectedRows.length === items.length) {
      setSelectedRows([])
    } else {
      setSelectedRows(items.map((concept) => concept.insuranceParamsId))
    }
  }

  const handleRowSelect = (id: InsuranceParamsVehicleGridItem['insuranceParamsId']) => {
    const isSelected = selectedRows.includes(id)
    let newSelectedRows = selectedRows

    if (isSelected) {
      // Remove when is currently selected
      newSelectedRows = selectedRows.filter((rowId) => rowId !== id)
    } else {
      // Add when not selected yet
      newSelectedRows = [...newSelectedRows, id]
    }

    setSelectedRows(() => newSelectedRows)
  }


  const singleRowModalAction = (paramsId: string, kind: SingleRowModalKind) => {
    singleRowModal.onClose()
    switch (kind) {
      case SingleRowModalKind.DeleteRow:
        deleteRow(paramsId)
        break
      case SingleRowModalKind.SendAccess:
        sendAccessForRow(paramsId)
        break
    }
  }

  const handleSendAccess = ({ insuranceParamsId: id, policyHolder }: InsuranceParamsVehicleGridItem) => {
    setModalParamsId(id)
    setSingleRowModalProps({
      kind: SingleRowModalKind.SendAccess,
      bodyText: `Skutečně chcete odeslat přístup k nabídce č. ${id} zákazníkovi ${formatPersonFullName(policyHolder)} ${formatPersonRcIco(policyHolder)}?`,
      okButtonText: 'Poslat',
      showCancelButton: true,
      title: 'Potvrzení',
      bodyUrl: undefined
    })
    singleRowModal.onOpen()
  }

  const sendAccessForRow = (paramsId: string) => {
    sendAccess([paramsId])
      .then((resp) => {
        reload()
        if (!resp.data.success && resp.data.errors) {
          toast({
            title: 'Nepodařilo se odeslat přístupové údaje pro nabídku: ' + resp.data.errors?.join(),
            status: 'error',
            duration: 5000,
            isClosable: true
          })
        }
        else {
          toast({
            title: 'Přístupové údaje byly odeslány',
            status: 'success',
            duration: 5000,
            isClosable: true
          })
        }
      })
  }

  const handleSendAccessMulti = () => {
    if (confirmSendAccess) {
      setIsSendingAccess.on()
      sendAccess(selectedRows)
        .then((resp) => {
          setIsSendingAccess.off()
          setSelectedRows([])
          setConfirmSendAccess.off()
          reload()
          if (!resp.data.success && resp.data.errors) {
            toast({
              title: 'Nepodařilo se odeslat přístupové údaje pro nabídky: ' + resp.data.errors?.join(),
              status: 'error',
              duration: 5000,
              isClosable: true
            })
          }
          else {
            toast({
              title: 'Přístupové údaje byly odeslány',
              status: 'success',
              duration: 5000,
              isClosable: true
            })
          }
        })
        .catch((err) => console.log('error delete param ids', err))
    } else {
      setConfirmSendAccess.on()
    }
  }

  const handleExport = () => {
    if (confirmExport) {
      setIsExporting.on()
      markForExport({ paramIds: selectedRows })
        .then((resp) => {
          setIsExporting.off()
          setSelectedRows([])
          setConfirmExport.off()
          reload()
          if (!resp.data.success && resp.data.errors) {
            toast({
              title: 'Nepodařilo se označit nabídky pro export: ' + resp.data.errors?.join(),
              status: 'error',
              duration: 5000,
              isClosable: true
            })
          }
          if (resp.data.importId) {
            window.location.href = getPoleasExportUrl(resp.data.importId)
          }
        })
        .catch((err) => console.log('error export', err))
    } else {
      setConfirmExport.on()
    }
  }

  const handleDisplayPublicUrl = (item: InsuranceParamsPoleasGridItem) => {
    generatePublicWizardUrl(item.insuranceParamsId)
      .then((response) => {
        setSingleRowModalProps({
          kind: SingleRowModalKind.PublicUrl,
          bodyText: `Pojistník: ${formatPersonFullName(item.policyHolder)}\n RČ/IČO: ${formatPersonRcIco(item.policyHolder)}`,
          okButtonText: 'OK',
          showCancelButton: false,
          title: 'Veřejný odkaz pro sjednání',
          bodyUrl: response.url
        })
        singleRowModal.onOpen()
      }).
      catch((err) => console.log('error generate public url', err))
  }

  const handleRowDelete = (id: InsuranceParamsVehicleGridItem['insuranceParamsId']) => {
    setModalParamsId(id)
    setSingleRowModalProps({
      kind: SingleRowModalKind.DeleteRow,
      bodyText: `Opravdu chcete smazat nabídku ${id}?`,
      okButtonText: 'Smazat',
      showCancelButton: true,
      title: 'Potvrzení',
      bodyUrl: undefined
    })
    singleRowModal.onOpen()
  }

  const deleteRow = (paramsId: string) => {
    deleteInsuranceParams({ paramIds: [paramsId] })
      .then((resp) => {
        reload()
        if (!resp.data.success && resp.data.errors) {
          toast({
            title: 'Nepodařilo se smazat nabídku: ' + resp.data.errors?.join(),
            status: 'error',
            duration: 5000,
            isClosable: true
          })
        }
      })
      .catch((err) => console.log('error delete param ids', err))
  }

  const handleToggleClientDisable = (item: InsuranceParamsPoleasGridItem) => {
    if (item.clientDisabled) {
      removeClientDisabledFlag(item.insuranceParamsId)
        .then((resp) => {
          reload()
          if (!resp.data.success) {
            toast({
              title: 'Nepodařilo se odblokovat nabídku',
              status: 'error',
              duration: 5000,
              isClosable: true
            })
          }
          else {
            toast({
              title: 'Nabídka byla odblokována',
              status: 'success',
              duration: 5000,
              isClosable: true
            })
          }
        })
        .catch((err) => console.log('error send access', err))
    }
    else {
      setModalParamsId(item.insuranceParamsId)
      setBlockOfferUser(`${formatPersonFullName(item.policyHolder)} ${formatPersonRcIco(item.policyHolder)}`)
      blockOfferModal.onOpen()
    }
  }

  const handleBlockClientAccessFormSubmit = (values: BlockClientAccessFormValues) => {
    const request: ClientDisabledRequest = {
      paramsId: modalParamsId,
      disableReason: values.reason,
      disableReasonText: values.reasonText
    }
    return addClientDisabledFlag(request)
      .then((resp) => {
        blockOfferModal.onClose()
        reload()
        if (!resp.data.success) {
          toast({
            title: 'Nepodařilo se zablokovat nabídku',
            status: 'error',
            duration: 5000,
            isClosable: true
          })
        }
        else {
          toast({
            title: 'Nabídka byla zablokována',
            status: 'success',
            duration: 5000,
            isClosable: true
          })
        }
      })
      .catch((err) => {
        blockOfferModal.onClose()
        console.log('error block offer', err)
      })
  }

  const handleDelete = () => {
    if (confirmDelete) {
      setIsDeleting.on()
      deleteInsuranceParams({ paramIds: selectedRows })
        .then((resp) => {
          setIsDeleting.off()
          setSelectedRows([])
          setConfirmDelete.off()
          reload()
          if (!resp.data.success && resp.data.errors) {
            toast({
              title: 'Nepodařilo se smazat nabídku: ' + resp.data.errors?.join(),
              status: 'error',
              duration: 5000,
              isClosable: true
            })
          }
        })
        .catch((err) => console.log('error delete param ids', err))
    } else {
      setConfirmDelete.on()
    }
  }

  return (
    <>
      <Box sx={{ display: 'grid', gap: 4 }}>
        <TableWrapper>
          <Table cellPadding="0" cellSpacing="0">
            <Thead>
              <TableRow hoverEffect={false}>
                <Th display={allowSelect ? 'table-cell' : 'none'} >
                  <Checkbox
                    isIndeterminate={isIndeterminate}
                    isChecked={isChecked}
                    onChange={handleCheckboxChange}
                  />
                </Th>

                <TableHeader>
                  <p>Číslo nabídky</p>
                  {filter.importId &&
                    <p>Id importu: <strong>{filter.importId}</strong></p>
                  }
                </TableHeader>

                <TableHeader>
                  <p>Import</p>
                  <p>Č. leas. smlouvy</p>
                </TableHeader>

                <TableHeader>
                  <p>Datum importu</p>
                  <p>Datum změny</p>
                </TableHeader>

                {/*  Space for vehicle manufacturer logo */}
                <TableHeader sx={{ px: 0 }}>
                </TableHeader>

                <TableHeader sx={{ pl: 0 }}>
                  <p>Pojistník</p>
                  {product == Product.VehicleInsurance && <p>Vozidlo</p>}
                </TableHeader>

                {/* Space for insurer */}
                <TableHeader>
                </TableHeader>

                <TableHeader>
                  Stav
                </TableHeader>


                {/* <!-- Space for status --> */}
                {/* <TableHeader></TableHeader> */}

                {/* Space for buttons */}
                <TableHeader></TableHeader>
              </TableRow>
            </Thead>

            <Tbody>
              {!loading && ((items?.length ?? 0) > 0) &&
                items.map((concept) => {
                  return <ImportRow
                    key={`${concept.insuranceParamsId}_${concept.offerId}`}
                    allowSelect={allowSelect}
                    selectedRows={selectedRows}
                    onRowSelect={handleRowSelect}
                    onSendAccess={handleSendAccess}
                    onToggleClientDisabled={handleToggleClientDisable}
                    onDisplayPublicUrl={handleDisplayPublicUrl}
                    onDeleteRow={handleRowDelete}
                    item={concept} />
                })}

              {!loading && ((items?.length ?? 0) == 0) &&
                <TableRow>
                  <TableData colSpan={9} py={8} textAlign={'center'}>
                    <Text>Žádné nabídky k zobrazení</Text>
                  </TableData>
                </TableRow>
              }

              {loading &&
                <TableRow>
                  <TableData colSpan={9} py={8}>
                    <Loading large={true} />
                  </TableData>
                </TableRow>
              }
            </Tbody>

            {(allowSelect || withPaginator) && !loading &&

              <Tfoot>
                <TableRow hoverEffect={false}>
                  <TableData colSpan={9}>
                    <Grid gridTemplateColumns='1fr auto 1fr' gap={2}>
                      {leftFooterPart && leftFooterPart}
                      {!leftFooterPart && <Box></Box>}

                      {withPaginator && (
                        <Pagination totalCount={totalCount} currentPage={currentPage} setCurrentPage={setCurrentPage} maxButtonCount={10} pageSize={pageSize} textAfterNumberOfRecords={deletedCountText} />
                      )}

                      {allowSelect && selectedRows.length > 0 && (
                        <>
                          <Flex sx={{ gap: 2 }}>
                            {confirmDelete && !isDeleting && (
                              <Button onClick={setConfirmDelete.off}>
                                Zrušit
                              </Button>
                            )}

                            <Button
                              colorScheme="red"
                              onClick={handleDelete}
                              disabled={isDeleting}
                            >
                              {/* <Trash /> */}
                              {!isDeleting && (
                                confirmDelete
                                  ? `Opravdu smazat? (${selectedRows.length})`
                                  : `Smazat (${selectedRows.length})`
                              )}
                              {isDeleting && 'Mazání...'}
                            </Button>
                          </Flex>

                          <Flex sx={{ gap: 2 }} justifyContent={'center'}>
                            {confirmSendAccess && !isSendingAccess && (
                              <Button onClick={setConfirmSendAccess.off}>
                                Zrušit
                              </Button>
                            )}

                            <Button
                              onClick={handleSendAccessMulti}
                              disabled={isSendingAccess}
                            >
                              {!isSendingAccess && (
                                confirmSendAccess
                                  ? `Opravdu poslat přístup ${selectedRows.length} uživatelům?`
                                  : `Poslat přístup (${selectedRows.length})`
                              )}
                              {isSendingAccess && 'Probíhá odesílání...'}
                            </Button>
                          </Flex>

                          {user?.poleas?.enableImport &&
                            <Flex sx={{ gap: 2 }} justifyContent={'end'}>
                              {confirmExport && !isExporting && (
                                <Button onClick={setConfirmExport.off}>
                                  Zrušit
                                </Button>
                              )}

                              <Button
                                onClick={handleExport}
                                disabled={isExporting}
                                whiteSpace='nowrap'
                              >
                                {!isExporting && (
                                  confirmExport
                                    ? 'Opravdu označit záznamy k exportu?'
                                    : `K exportu (${selectedRows.length})`
                                )}
                                {isExporting && 'Probíhá export...'}
                              </Button>
                            </Flex>
                          }
                          {!user?.poleas?.enableImport &&
                            <Box></Box>
                          }
                        </>
                      )}
                    </Grid>
                  </TableData>
                </TableRow>
              </Tfoot>}
          </Table>
        </TableWrapper >
      </Box>

      <AlertDialog
        isOpen={singleRowModal.isOpen}
        leastDestructiveRef={sigleRowModalCancelRef}
        onClose={singleRowModal.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              {singleRowModalProps.title}
            </AlertDialogHeader>

            <AlertDialogBody whiteSpace='pre-line'>
              {singleRowModalProps.bodyText}

              {singleRowModalProps.bodyUrl &&
                <>
                  <br />
                  <span>Odkaz: </span>
                  <a href={singleRowModalProps.bodyUrl} rel="noopener noreferrer" target="_blank">{singleRowModalProps.bodyUrl}</a>
                </>
              }
            </AlertDialogBody>

            <AlertDialogFooter>
              {singleRowModalProps.showCancelButton &&
                <Button colorScheme='gray' ref={sigleRowModalCancelRef} onClick={singleRowModal.onClose}>
                  Zrušit
                </Button>
              }
              <Button onClick={() => { singleRowModalAction(modalParamsId, singleRowModalProps.kind) }} ml={3}>
                {singleRowModalProps.okButtonText}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Modal isOpen={blockOfferModal.isOpen} onClose={blockOfferModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />

          <ModalHeader>Doplnění informací k blokaci</ModalHeader>

          <ModalBody>
            <BlockClientAccessForm onSubmit={handleBlockClientAccessFormSubmit} onCancel={blockOfferModal.onClose} userText={blockOfferUser} />
          </ModalBody>

          <ModalFooter></ModalFooter>
        </ModalContent>
      </Modal>

    </>
  )
}
